import {
  RecordContextProvider,
  useGetIdentity,
  useListContext,
  useTranslate,
} from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { ExportButton } from "@/components/admin/export-button";
import { InfiniteListBase } from "ra-core";
import { SortButton } from "@/components/admin/sort-button";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import type { Company } from "../types";
import { CompanyCard } from "./CompanyCard";
import { CompanyEmpty } from "./CompanyEmpty";
import { CompanyListFilter } from "./CompanyListFilter";
import { useCompanyFilterBar } from "./companyFilterBar";
import { ActiveFilterBar } from "../misc/ActiveFilterBar";

// NOTE: this list has no search box. A `companyFilters` array holding a SearchInput
// used to be passed to InfiniteListBase as `filters`, a prop it does not have (it is
// headless and takes `filter`, a permanent filter object), so it was silently dropped
// and the input never rendered. Removed rather than wired up: mobile is out of scope
// for now. Adding search means rendering an input inside MobileCompanyListLayout, the
// way CompanyListFilter is rendered, not passing it here.

export const MobileCompanyList = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase perPage={25} sort={{ field: "name", order: "ASC" }}>
      <MobileCompanyListLayout />
    </InfiniteListBase>
  );
};

const MobileCompanyListLayout = () => {
  const translate = useTranslate();
  const { isPending, isFetching, data, filterValues } =
    useListContext<Company>();
  const filterBar = useCompanyFilterBar();
  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  // Wait for a refetch before showing the onboarding screen: removing the last
  // filter from an empty result would otherwise flash it before the rows land.
  if (!isPending && !isFetching && !data?.length && !hasFilters) {
    return (
      <>
        <MobileHeader>
          <h1 className="text-xl font-semibold">
            {translate("resources.companies.name", { smart_count: 2 })}
          </h1>
        </MobileHeader>
        <MobileContent>
          <CompanyEmpty />
        </MobileContent>
      </>
    );
  }

  return (
    <div>
      <MobileHeader>
        <CompanyListFilter />
      </MobileHeader>
      <MobileContent>
        <ActiveFilterBar {...filterBar} className="mb-3" />
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-semibold">
            {translate("resources.companies.name", { smart_count: 2 })}
          </h1>
          <div className="flex items-center gap-1">
            <SortButton fields={["name", "created_at", "nb_contacts"]} />
            <ExportButton />
            <CreateButton
              label={translate("resources.companies.action.new", {
                _: "New",
              })}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {data?.map((company) => (
            <RecordContextProvider key={company.id} value={company}>
              <CompanyCard />
            </RecordContextProvider>
          ))}
        </div>
        {(!data || data.length === 0) && hasFilters && (
          <div className="p-4 text-muted-foreground text-sm">
            {translate("resources.companies.empty.title", {
              _: "No companies found",
            })}
          </div>
        )}
        <div className="flex justify-center mt-4">
          <InfinitePagination />
        </div>
      </MobileContent>
    </div>
  );
};
